import { Box, Typography } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';

export type StatusChipVariant =
  | 'active'
  | 'inactive'
  | 'maintenance'
  | 'deprecated'
  | 'provisioning'
  | 'error'
  | 'success'
  | 'warning'
  | 'info';

interface StatusChipProps {
  label: string;
  variant: StatusChipVariant;
}

function resolveStatusTone(theme: Theme, variant: StatusChipVariant): string {
  if (variant === 'active' || variant === 'success') {
    return theme.palette.success.main;
  }

  if (variant === 'inactive' || variant === 'error') {
    return theme.palette.error.main;
  }

  if (variant === 'maintenance' || variant === 'warning') {
    return theme.palette.warning.main;
  }

  if (variant === 'deprecated') {
    return theme.palette.text.secondary;
  }

  return theme.palette.info.main;
}

export function StatusChip({ label, variant }: StatusChipProps) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1,
        py: 0.375,
        borderRadius: 999,
        bgcolor: (theme) => {
          const tone = resolveStatusTone(theme, variant);
          return alpha(tone, theme.palette.mode === 'dark' ? 0.24 : 0.12);
        },
        border: '1px solid',
        borderColor: (theme) => {
          const tone = resolveStatusTone(theme, variant);
          return alpha(tone, theme.palette.mode === 'dark' ? 0.48 : 0.3);
        },
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: (theme) => resolveStatusTone(theme, variant),
        }}
      />
      <Typography
        component="span"
        variant="chip"
        sx={{ color: (theme) => resolveStatusTone(theme, variant) }}
      >
        {label}
      </Typography>
    </Box>
  );
}
